import React, { useMemo, useState } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/pt';
import {
  Box,
  Typography,
  Chip,
  useTheme,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  PlayCircleFilled as PlayCircleFilledIcon,
  EventNote as EventNoteIcon,
  Person as PersonIcon,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './AppointmentCalendar.css';

const AppointmentCalendar = ({ 
  appointments = [], 
  onSelectEvent, 
  onStartVideoCall,
  onViewDetails,
  onDayClick,
  language = 'en',
  t = {}
}) => {
  const theme = useTheme();
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  // Add state for current date and view navigation
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState('month');

  // Set moment locale based on language
  const localizer = useMemo(() => {
    moment.locale(language === 'pt' ? 'pt' : 'en');
    return momentLocalizer(moment);
  }, [language]);

  // Define color scheme for different appointment statuses - wrapped in useMemo to fix dependency warning
  const statusColors = useMemo(() => ({
    SCHEDULED: {
      background: alpha(theme.palette.warning.main, 0.15),
      border: theme.palette.warning.main,
      text: theme.palette.warning.dark,
      icon: EventNoteIcon
    },
    CONFIRMED: {
      background: alpha(theme.palette.success.main, 0.15),
      border: theme.palette.success.main,
      text: theme.palette.success.dark,
      icon: CheckCircleIcon
    },
    ONGOING: {
      background: alpha(theme.palette.primary.main, 0.15),
      border: theme.palette.primary.main,
      text: theme.palette.primary.dark,
      icon: PlayCircleFilledIcon
    },
    COMPLETED: {
      background: alpha(theme.palette.info.main, 0.15),
      border: theme.palette.info.main,
      text: theme.palette.info.dark,
      icon: CheckCircleIcon
    },
    AVAILABLE: {
      background: alpha(theme.palette.grey[500], 0.15),
      border: theme.palette.grey[500],
      text: theme.palette.grey[700],
      icon: EventNoteIcon
    }
  }), [theme.palette]);

  // Calculate daily appointment counts by status for monthly view
  const dailyAppointmentCounts = useMemo(() => {
    const counts = {};
    
    appointments.forEach(appointment => {
      let dateKey;
      
      if (appointment.status === 'COMPLETED' && appointment.completedAt) {
        // Convert UTC to local date for proper grouping
        const localDate = new Date(appointment.completedAt);
        dateKey = `${localDate.getFullYear()}-${String(localDate.getMonth() + 1).padStart(2, '0')}-${String(localDate.getDate()).padStart(2, '0')}`;
      } else if (appointment.confirmedSlot) {
        const localDate = new Date(appointment.confirmedSlot);
        dateKey = `${localDate.getFullYear()}-${String(localDate.getMonth() + 1).padStart(2, '0')}-${String(localDate.getDate()).padStart(2, '0')}`;
      } else if (appointment.scheduledAt) {
        const localDate = new Date(appointment.scheduledAt);
        dateKey = `${localDate.getFullYear()}-${String(localDate.getMonth() + 1).padStart(2, '0')}-${String(localDate.getDate()).padStart(2, '0')}`;
      } else {
        const localDate = new Date(appointment.createdAt || Date.now());
        dateKey = `${localDate.getFullYear()}-${String(localDate.getMonth() + 1).padStart(2, '0')}-${String(localDate.getDate()).padStart(2, '0')}`;
      }
      
      const status = appointment.status?.toUpperCase() || 'SCHEDULED';
      
      if (!counts[dateKey]) {
        counts[dateKey] = { SCHEDULED: 0, CONFIRMED: 0, ONGOING: 0, COMPLETED: 0, AVAILABLE: 0 };
      }
      counts[dateKey][status] = (counts[dateKey][status] || 0) + 1;
    });
    
    return counts;
  }, [appointments]);

  // Transform appointments into calendar events
  const calendarEvents = useMemo(() => {
    return appointments.map(appointment => {
      // Determine the date to use
      let startDate;
      let endDate;
      
      if (appointment.status === 'COMPLETED' && appointment.completedAt) {
        startDate = new Date(appointment.completedAt);
        endDate = new Date(startDate.getTime() + 60 * 60 * 1000); // 1 hour duration
      } else if (appointment.confirmedSlot) {
        startDate = new Date(appointment.confirmedSlot);
        endDate = new Date(startDate.getTime() + 60 * 60 * 1000); // 1 hour duration
      } else if (appointment.scheduledAt) {
        startDate = new Date(appointment.scheduledAt);
        endDate = new Date(startDate.getTime() + 60 * 60 * 1000); // 1 hour duration
      } else {
        // Fallback to creation date
        startDate = new Date(appointment.createdAt || Date.now());
        endDate = new Date(startDate.getTime() + 60 * 60 * 1000);
      }

      const appointmentStatus = appointment.status?.toUpperCase() || 'SCHEDULED';
      const colors = statusColors[appointmentStatus] || statusColors.SCHEDULED;

      return {
        id: appointment.requestId,
        title: `${appointment.patientName || `Patient ${appointment.userId?.slice(-8)}`}`,
        start: startDate,
        end: endDate,
        resource: {
          ...appointment,
          colors,
          status: appointmentStatus
        }
      };
    });
  }, [appointments, statusColors]);

  // Custom event component - simplified
  const EventComponent = ({ event }) => {
    const { colors } = event.resource;
    
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          p: 0.5,
          height: '100%',
          fontSize: '0.7rem',
          color: colors.text,
          fontWeight: 500,
          overflow: 'hidden'
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.7rem', textOverflow: 'ellipsis', overflow: 'hidden' }}>
          {event.title}
        </Typography>
      </Box>
    );
  };

  // Custom month date cell component to show appointment counts
  const MonthDateCell = ({ value, children }) => {
    // Use consistent date formatting with local timezone
    const localDate = new Date(value);
    const dateKey = `${localDate.getFullYear()}-${String(localDate.getMonth() + 1).padStart(2, '0')}-${String(localDate.getDate()).padStart(2, '0')}`;
    const counts = dailyAppointmentCounts[dateKey];
    
    if (!counts || currentView !== 'month') {
      return <div className="rbc-date-cell">{children}</div>;
    }

    const totalCount = Object.values(counts).reduce((sum, count) => sum + count, 0);
    
    // Get status labels for display
    const getShortStatusLabel = (status) => {
      const labels = {
        en: {
          SCHEDULED: 'Scheduled',
          CONFIRMED: 'Confirmed', 
          ONGOING: 'Ongoing',
          COMPLETED: 'Completed',
          AVAILABLE: 'Available'
        },
        pt: {
          SCHEDULED: 'Agendado',
          CONFIRMED: 'Confirmado',
          ONGOING: 'Em Curso', 
          COMPLETED: 'Concluído',
          AVAILABLE: 'Disponível'
        }
      };
      return labels[language]?.[status] || status;
    };
    
    return (
      <div className="rbc-date-cell" style={{ position: 'relative', paddingBottom: '20px' }}>
        {children}
        {totalCount > 0 && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 2,
              left: 2,
              right: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.25,
              alignItems: 'stretch'
            }}
          >
            {Object.entries(counts).map(([status, count]) => {
              if (count === 0) return null;
              const colors = statusColors[status];
              const statusLabel = getShortStatusLabel(status);
              return (
                <Box
                  key={status}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    minHeight: 16,
                    borderRadius: 8,
                    backgroundColor: colors.border,
                    color: 'white',
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    px: 0.5,
                    py: 0.25,
                    lineHeight: 1
                  }}
                >
                  <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 600, color: 'white' }}>
                    {statusLabel}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 600, color: 'white' }}>
                    {count}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}
      </div>
    );
  };

  // Handle event selection - now shows popup first
  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    setDialogOpen(true);
    // Don't call onSelectEvent immediately - wait for details button
  };

  // Handle day click - navigate to day view for that date or trigger onDayClick
  const handleSelectSlot = ({ start, end, slots, action }) => {
    if (action === 'click' || action === 'doubleClick') {
      if (onDayClick) {
        // Check if the clicked date is in the past
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const clickedDate = new Date(start);
        clickedDate.setHours(0, 0, 0, 0);
        
        if (clickedDate < today) {
          // For past dates, navigate to day view instead of showing add dialog
          setCurrentDate(start);
          setCurrentView('day');
        } else {
          // For current/future dates, use onDayClick for adding availability
          onDayClick(start);
        }
      } else {
        // Otherwise, just navigate to day view
        setCurrentDate(start);
        setCurrentView('day');
      }
    }
  };

  // Navigation handlers
  const handleNavigate = (newDate) => {
    setCurrentDate(newDate);
  };

  const handleViewChange = (view) => {
    setCurrentView(view);
  };

  // Custom event style getter - simplified
  const eventStyleGetter = (event) => {
    const { colors } = event.resource;
    return {
      style: {
        backgroundColor: colors.background,
        borderLeft: `3px solid ${colors.border}`,
        borderRadius: '3px',
        border: 'none',
        color: colors.text,
        padding: '1px 3px',
        fontSize: '0.7rem',
        fontWeight: '500'
      }
    };
  };

  // Format event time for dialog
  const formatEventTime = (date) => {
    return moment(date).format(language === 'pt' ? 'DD/MM/YYYY HH:mm' : 'MM/DD/YYYY HH:mm');
  };

  // Get status label
  const getStatusLabel = (status) => {
    const labels = {
      en: {
        SCHEDULED: 'Scheduled',
        CONFIRMED: 'Confirmed',
        ONGOING: 'Ongoing',
        COMPLETED: 'Completed',
        AVAILABLE: 'Available'
      },
      pt: {
        SCHEDULED: 'Agendado',
        CONFIRMED: 'Confirmado',
        ONGOING: 'Em Curso',
        COMPLETED: 'Concluído',
        AVAILABLE: 'Disponível'
      }
    };
    return labels[language]?.[status] || status;
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedEvent(null);
  };

  const handleStartVideoCall = () => {
    if (selectedEvent && onStartVideoCall) {
      onStartVideoCall(selectedEvent.resource.requestId);
    }
    handleDialogClose();
  };

  const handleViewDetails = () => {
    if (selectedEvent && onViewDetails) {
      onViewDetails(selectedEvent.resource);
    }
    if (selectedEvent && onSelectEvent) {
      onSelectEvent(selectedEvent.resource);
    }
    handleDialogClose();
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Simplified Legend */}
      <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
          {language === 'pt' ? 'Status dos Agendamentos' : 'Appointment Status'}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {Object.entries(statusColors).map(([status, colors]) => (
            <Box key={status} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  backgroundColor: colors.background,
                  borderLeft: `3px solid ${colors.border}`,
                  borderRadius: 0.5
                }}
              />
              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                {getStatusLabel(status)}
              </Typography>
            </Box>
          ))}
        </Box>
        {currentView === 'month' && (
          <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
            {onDayClick 
              ? (language === 'pt' 
                  ? '💡 Clique em hoje/dias futuros para adicionar disponibilidade, ou em dias passados para ver detalhes. Os números coloridos mostram a quantidade de agendamentos por status.'
                  : '💡 Click on today/future days to add availability, or on past days to view details. Colored numbers show appointment counts by status.'
                )
              : (language === 'pt' 
                  ? '💡 Clique em um dia para ver os detalhes. Os números coloridos mostram a quantidade de agendamentos por status.'
                  : '💡 Click on a day to view details. Colored numbers show appointment counts by status.'
                )
            }
          </Typography>
        )}
      </Box>

      {/* Simplified Calendar Container */}
      <Box sx={{ 
        border: '1px solid', 
        borderColor: 'grey.200', 
        borderRadius: 1, 
        bgcolor: 'white',
        '& .rbc-calendar': {
          border: 'none',
          borderRadius: 0
        }
      }}>
        <Box sx={{ height: 500 }}>
          <Calendar
            localizer={localizer}
            events={calendarEvents}
            startAccessor="start"
            endAccessor="end"
            onSelectEvent={handleSelectEvent}
            onSelectSlot={handleSelectSlot}
            selectable={true}
            eventPropGetter={eventStyleGetter}
            components={{
              event: EventComponent,
              dateCellWrapper: MonthDateCell
            }}
            views={['month', 'week', 'day']}
            view={currentView}
            date={currentDate}
            onNavigate={handleNavigate}
            onView={handleViewChange}
            popup={false}
            culture={language === 'pt' ? 'pt' : 'en'}
            messages={{
              next: language === 'pt' ? '›' : '›',
              previous: language === 'pt' ? '‹' : '‹',
              today: language === 'pt' ? 'Hoje' : 'Today',
              month: language === 'pt' ? 'Mês' : 'Month',
              week: language === 'pt' ? 'Semana' : 'Week',
              day: language === 'pt' ? 'Dia' : 'Day',
              agenda: language === 'pt' ? 'Agenda' : 'Agenda',
              date: language === 'pt' ? 'Data' : 'Date',
              time: language === 'pt' ? 'Hora' : 'Time',
              event: language === 'pt' ? 'Evento' : 'Event',
              noEventsInRange: language === 'pt' ? 'Nenhum agendamento neste período' : 'No appointments in this range'
            }}
            formats={{
              dayFormat: (date, culture, localizer) =>
                localizer.format(date, 'DD', culture),
              dayRangeHeaderFormat: ({ start, end }, culture, localizer) =>
                localizer.format(start, language === 'pt' ? 'DD MMM' : 'MMM DD', culture) + 
                ' – ' + 
                localizer.format(end, language === 'pt' ? 'DD MMM' : 'MMM DD', culture),
              monthHeaderFormat: (date, culture, localizer) =>
                localizer.format(date, language === 'pt' ? 'MMMM YYYY' : 'MMMM YYYY', culture)
            }}
            style={{
              fontFamily: theme.typography.fontFamily,
              fontSize: '0.8rem'
            }}
          />
        </Box>
      </Box>

      {/* Enhanced Event Details Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleDialogClose}
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 1 }
        }}
      >
        {selectedEvent && (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon color="primary" sx={{ fontSize: '1.2rem' }} />
                <Typography variant="h6" component="div" sx={{ flex: 1 }}>
                  {selectedEvent.title}
                </Typography>
                <Chip 
                  label={getStatusLabel(selectedEvent.resource.status)}
                  size="small"
                  sx={{ 
                    backgroundColor: selectedEvent.resource.colors.background,
                    color: selectedEvent.resource.colors.text,
                    fontWeight: 500,
                    fontSize: '0.7rem'
                  }}
                />
              </Box>
            </DialogTitle>
            
            <DialogContent sx={{ pt: 1 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AccessTimeIcon color="action" sx={{ fontSize: '1rem' }} />
                  <Typography variant="body2">
                    <strong>{language === 'pt' ? 'Horário:' : 'Time:'}</strong> {formatEventTime(selectedEvent.start)}
                  </Typography>
                </Box>
                
                {selectedEvent.resource.reason && (
                  <Typography variant="body2">
                    <strong>{language === 'pt' ? 'Motivo:' : 'Reason:'}</strong> {selectedEvent.resource.reason}
                  </Typography>
                )}
                
                {selectedEvent.resource.userId && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>{language === 'pt' ? 'ID:' : 'ID:'}</strong> {selectedEvent.resource.userId.slice(-8)}
                  </Typography>
                )}
              </Box>
            </DialogContent>
            
            <Divider />
            
            <DialogActions sx={{ p: 2, gap: 1 }}>
              <Button onClick={handleDialogClose} size="small">
                {language === 'pt' ? 'Fechar' : 'Close'}
              </Button>
              
              {selectedEvent.resource.status === 'CONFIRMED' && (
                <Button 
                  onClick={handleStartVideoCall}
                  variant="contained"
                  size="small"
                  startIcon={<PlayCircleFilledIcon />}
                >
                  {language === 'pt' ? 'Videochamada' : 'Video Call'}
                </Button>
              )}
              
              <Button 
                onClick={handleViewDetails}
                variant="outlined"
                size="small"
              >
                {language === 'pt' ? 'Ver Detalhes' : 'View Details'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default AppointmentCalendar; 